// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { NumberUtils } from '@shared/util/number-utils';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { CustomImageMaterialData } from '@3d-scene/model3d/cache/custom-image-material-data';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MetaElementUsageIdentityVO, MetaElementVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { CustomImageDataVO } from '@project/components/article/model/vo/custom-article.vo';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { ContainerService } from '../model3d/container.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectModel } from '@project/model/project.model';
import { forkJoin, Observable, of, Subscriber } from 'rxjs';
import { CustomArticleService } from '@article/service/custom-article.service';
import { mergeMap } from 'rxjs/operators';
import { ImageProcessingData } from './image-processing-data';
import { StringUtils } from '@shared/util/string-utils';
import { Injectable } from '@angular/core';
import { ProjectUserModel } from '@project/model/project-user.model';

@Injectable({ providedIn: 'root' })
export class ImageTextureProcessor {
  private static readonly CUSTOM_TEXTURE_NAME: string = 'customTexture';
  private static readonly IMAGE_META_ELEMENTS: string[] = [TextureAreaVO.TYPE_IMAGE, TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE];

  private imagesCache: Dictionary<string, CustomImageMaterialData> = new Dictionary<string, CustomImageMaterialData>();
  private imageOwnerCache: Dictionary<string, MetaElementUsageIdentityVO[]> = new Dictionary<string, MetaElementUsageIdentityVO[]>();

  constructor(
    private projectModel: ProjectModel,
    private projectUserModel: ProjectUserModel,
    private customArticleService: CustomArticleService,
    private containerService: ContainerService
  ) {}

  public loadAndApplyImagesToConnections(connections: Connection[]): Observable<any> {
    if (ArrayUtils.isEmpty(connections) || !this.projectModel.texturesEnabled) {
      return of(connections);
    }
    const imageAreaRequest: Observable<any>[] = [];
    for (const connection of connections) {
      imageAreaRequest.push(this.createRequestForConnection(connection));
    }
    return forkJoin(imageAreaRequest);
  }

  private createRequestForConnection(connection: Connection): Observable<any> {
    const articleContainer: ArticleContainer = connection.articleContainer;
    const userId: number = this.getUserIdForImageRequest(articleContainer.articleUsage);
    const imageArea: MetaElementVO = connection.metaElementVO;
    if (imageArea instanceof TextureAreaVO && ImageTextureProcessor.IMAGE_META_ELEMENTS.indexOf(imageArea.textureType) !== -1) {
      return this.createRequestForImageArea(imageArea, userId, articleContainer);
    } else {
      return of(null);
    }
  }

  private getUserIdForImageRequest(articleUsage: ArticleUsageVO): number {
    let userId: number = CustomArticleUtils.getCustomImageUserId(articleUsage);
    if (!NumberUtils.isNumber(userId)) {
      userId = this.projectUserModel.getProjectOwnerId();
    }
    return userId;
  }

  private createRequestForImageArea(imageArea: MetaElementVO, userId: number, articleContainer: ArticleContainer): Observable<any> {
    const customImageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
      articleContainer.articleUsage,
      imageArea.name,
      DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
    );
    if (customImageHash) {
      if (this.imagesCache.containsKey(customImageHash)) {
        return this.applyImageToImageArea(customImageHash, imageArea, articleContainer);
      } else {
        const imageProcessingData: ImageProcessingData = new ImageProcessingData(userId, customImageHash, articleContainer);
        return this.customArticleService.requestLoadingCustomArticleImage(imageProcessingData).pipe(
          mergeMap((fullImageProcessingData: ImageProcessingData) => {
            return this.createLoadedImage(fullImageProcessingData);
          }),
          mergeMap((image: HTMLImageElement) => {
            this.createAndSaveCachedObject(customImageHash, image);
            return this.applyImageToImageArea(customImageHash, imageArea, articleContainer);
          })
        );
      }
    } else {
      return of(null);
    }
  }

  private applyImageToImageArea(customImageHash: string, imageArea: MetaElementVO, articleContainer: ArticleContainer): Observable<any> {
    const connectionName: string = imageArea.name;
    const imageConnection: Connection = articleContainer.findConnectionByName(connectionName);
    if (!imageConnection) {
      return of(null);
    }
    this.updateImageOwners(customImageHash, imageConnection);
    if (imageConnection.type === TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE || imageConnection.isDyeable()) {
      return this.applyTextureToConnectionAsync(customImageHash, imageConnection);
    } else if (imageConnection.type === TextureAreaVO.TYPE_IMAGE) {
      return this.applyTextureToArticle(customImageHash, articleContainer, connectionName);
    }
    return of(null);
  }

  private createLoadedImage(data: ImageProcessingData): Observable<any> {
    if (!data.customImageData) {
      return of(null);
    }
    const customImageData: CustomImageDataVO = data.customImageData;
    let textureName: string = ImageTextureProcessor.CUSTOM_TEXTURE_NAME;
    if (customImageData.imageType) {
      textureName += '.' + customImageData.imageType;
    }
    return TextureUtils.createImage(customImageData.imageData, customImageData.imageType, textureName);
  }

  private applyTextureToArticle(imageHash: string, articleContainer: ArticleContainer, connectionName: string): Observable<any> {
    return new Observable((subscriber: Subscriber<any>) => {
      const articleUsage: ArticleUsageVO = articleContainer.articleUsage;
      const articleWidth: number = CustomArticleUtils.getCustomArticleWidth(articleUsage);
      const articleDepth: number = CustomArticleUtils.getCustomArticleDepth(articleUsage);
      let scale: number = 1;
      const customImageData: CustomImageMaterialData = this.imagesCache.getValue(imageHash);
      const imageConnection: Connection = articleContainer.findConnectionByName(connectionName);
      imageConnection.isTextureLoaded = true;
      if (customImageData) {
        imageConnection.isImageLoaded = true;
        // if the image doesn't fit the article then we need to scale it down preserving the image aspect ratio
        if (articleWidth < customImageData.width || articleDepth < customImageData.height) {
          // find out what image dimension is leading for resizing and calculating the scale
          if (customImageData.width / articleWidth >= customImageData.height / articleDepth) {
            scale = articleWidth / customImageData.width;
          } else {
            scale = articleDepth / customImageData.height;
          }
        }

        // the connection width coincides with the image height
        const connectionWidth: number = customImageData.height * scale;
        const connectionHeight: number = customImageData.width * scale;
        this.containerService.resizeConnection(articleContainer.findConnectionByName(connectionName), connectionWidth, connectionHeight);
        imageConnection.imageBitmap = customImageData.image;
        imageConnection.createMeshTextureMaterial();
      }
      subscriber.next(null);
      subscriber.complete();
    });
  }

  public applyTextureToConnection(imageHash: string, connection: Connection): void {
    const customImageData: CustomImageMaterialData = this.imagesCache.getValue(imageHash);
    connection.isTextureLoaded = true;
    if (customImageData) {
      connection.isImageLoaded = true;
      customImageData.height = connection.compositionRectangle.height;
      customImageData.width = connection.compositionRectangle.width;
      connection.imageBitmap = customImageData.image;
      connection.refreshTexture();
    }
  }

  private applyTextureToConnectionAsync(imageHash: string, connection: Connection): Observable<any> {
    return new Observable((subscriber: Subscriber<any>) => {
      this.applyTextureToConnection(imageHash, connection);
      subscriber.next(null);
      subscriber.complete();
    });
  }

  public createAndSaveCachedObject(imageHash: string, image: HTMLImageElement): void {
    if (!image || StringUtils.isEmpty(imageHash)) {
      return;
    }
    const customImageMaterialData: CustomImageMaterialData = new CustomImageMaterialData(image, image.width, image.height);
    this.imagesCache.setValue(imageHash, customImageMaterialData);
  }

  public clearCache(): void {
    this.imagesCache = new Dictionary<string, CustomImageMaterialData>();
    this.imageOwnerCache = new Dictionary<string, MetaElementUsageIdentityVO[]>();
  }

  private updateImageOwners(imageHash: string, connection: Connection): void {
    const articleContainer: ArticleContainer = connection.articleContainer;
    if (!articleContainer || !this.imagesCache.containsKey(imageHash)) {
      return;
    }
    const ownerMetaElementUsageIdentity: MetaElementUsageIdentityVO = MetaElementUsageIdentityVO.create(
      articleContainer.articleGUID,
      connection.connectionName
    );
    const imageOwners: MetaElementUsageIdentityVO[] = this.imageOwnerCache.getValue(imageHash);
    if (!imageOwners) {
      this.imageOwnerCache.setValue(imageHash, [ownerMetaElementUsageIdentity]);
    } else {
      imageOwners.push(ownerMetaElementUsageIdentity);
    }
  }

  public articleContainerDetachedHandler(articleContainer: ArticleContainer): void {
    const imageAreas: TextureAreaVO[] = this.getImageAreas(articleContainer.article);
    for (const imageArea of imageAreas) {
      const customImageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        articleContainer.articleUsage,
        imageArea.name,
        DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
      );
      if (customImageHash) {
        const checkedMetaElementUsageId: MetaElementUsageIdentityVO = MetaElementUsageIdentityVO.create(articleContainer.articleGUID, imageArea.name);
        this.cleanImageCache(customImageHash, checkedMetaElementUsageId);
      }
    }
  }

  private getImageAreas(article: ArticleVO): TextureAreaVO[] {
    return ArticleUtils.getImageUsageTextureElements(article);
  }

  private cleanImageCache(customImageHash: string, checkedMetaElementUsageId: MetaElementUsageIdentityVO): void {
    const metaElementIds: MetaElementUsageIdentityVO[] = this.imageOwnerCache.getValue(customImageHash);
    if (!metaElementIds) {
      return;
    }
    for (const metaElementId of metaElementIds) {
      if (checkedMetaElementUsageId.equals(metaElementId)) {
        metaElementIds.splice(metaElementIds.indexOf(metaElementId), 1);
        break;
      }
    }
    if (metaElementIds.length === 0) {
      this.imageOwnerCache.remove(customImageHash);
      this.imagesCache.remove(customImageHash);
    }
  }

  public cleanConnectionImageCache(connectionCubes: Connection[]): void {
    if (ArrayUtils.isEmpty(connectionCubes)) {
      return;
    }
    for (const connectionCube of connectionCubes) {
      const articleContainer: ArticleContainer = connectionCube.articleContainer;
      const customImageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
        articleContainer.articleUsage,
        connectionCube.connectionName,
        DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
      );
      if (customImageHash) {
        const checkedMetaElementUsageId: MetaElementUsageIdentityVO = MetaElementUsageIdentityVO.create(
          articleContainer.articleGUID,
          connectionCube.connectionName
        );
        this.cleanImageCache(customImageHash, checkedMetaElementUsageId);
      }
    }
  }
}
